import { useCallback, useEffect, useRef, useState } from "react";
import * as O from "@/components/Ornaments";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  GuestPhoto,
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ═══════════════════════════════════════════════════════════════════════
// ACADEMIA — Sevinch Karimova's TUIT graduation site (g01).
//
// The variant wears the gravitas of an Oxford / Harvard graduation
// programme adapted for a Tashkent university: cream paper, Oxford
// navy, Oxford red, Cormorant SC small caps. The hero opens with an
// academic seal — Ornaments.Wreath circling the honoree's name — that
// stamps itself in (opacity + scale 0.9 → 1 on first paint), and a
// diploma-scroll line that "unrolls" beneath via clip-path expansion.
// A unique Achievements list (numbered hairline rows) sits between the
// Story and Gallery, listing GPA, hackathon placement, diploma project,
// honours and student-council role. NO music — academic context
// honoured with dignified silence. Sound is restrained: chime on RSVP
// yes, taps on RSVP no + share buttons. All motion is gated by
// prefers-reduced-motion.
// ═══════════════════════════════════════════════════════════════════════

// ─── prefers-reduced-motion hook (no extra deps) ──────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

// ─── IntersectionObserver reveal wrapper ──────────────────────────────
interface RevealProps {
  children: React.ReactNode;
  reduce: boolean;
  delay?: number;
}
function Reveal({ children, reduce, delay = 0 }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: reduce
          ? "none"
          : `opacity 1.0s ease-out ${delay}ms, transform 1.0s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── Diploma + tassel SVG illustration ────────────────────────────────
// A tightly-rolled scroll with a navy ribbon and a red tassel hanging
// at one side — pure SVG, scales crisply at any viewport.
function DiplomaIllustration({ ink, accent, size = 220 }: { ink: string; accent: string; size?: number }) {
  return (
    <svg width={size} height={size * 0.9} viewBox="0 0 220 200" aria-hidden style={{ display: "block" }}>
      {/* Scroll body */}
      <g>
        {/* Inner roll cylinder shadows */}
        <ellipse cx="40" cy="100" rx="18" ry="60" fill={ink} opacity="0.08" />
        <ellipse cx="180" cy="100" rx="18" ry="60" fill={ink} opacity="0.08" />
        {/* Main parchment rectangle */}
        <rect x="40" y="44" width="140" height="112" fill="#FBF6EC" stroke={ink} strokeWidth="0.8" />
        {/* Top + bottom roll cylinders */}
        <ellipse cx="40" cy="44" rx="6" ry="60" fill="#FBF6EC" stroke={ink} strokeWidth="0.7" />
        <ellipse cx="180" cy="44" rx="6" ry="60" fill="#FBF6EC" stroke={ink} strokeWidth="0.7" />
        <ellipse cx="40" cy="156" rx="6" ry="4" fill="#FBF6EC" stroke={ink} strokeWidth="0.7" />
        <ellipse cx="180" cy="156" rx="6" ry="4" fill="#FBF6EC" stroke={ink} strokeWidth="0.7" />

        {/* Hairlines on the parchment */}
        {[60, 80, 100, 120, 140].map((y) => (
          <line key={y} x1="56" y1={y} x2="164" y2={y} stroke={ink} strokeWidth="0.3" opacity="0.35" />
        ))}

        {/* "DIPLOMA" eyebrow */}
        <text
          x="110"
          y="58"
          textAnchor="middle"
          fontFamily="var(--font-ui)"
          fontSize="6"
          fontWeight="700"
          letterSpacing="0.4em"
          fill={accent}
        >
          DIPLOMA
        </text>

        {/* Wax seal */}
        <g transform="translate(108 132)">
          <circle r="11" fill={accent} stroke={ink} strokeWidth="0.6" />
          <circle r="7" fill="none" stroke="#FBF6EC" strokeWidth="0.5" opacity="0.7" />
          <path d="M -3 -3 L 3 3 M -3 3 L 3 -3" stroke="#FBF6EC" strokeWidth="0.7" />
        </g>
      </g>

      {/* Navy ribbon wrapping the scroll */}
      <g>
        <rect x="34" y="92" width="152" height="14" fill={ink} />
        <rect x="34" y="92" width="152" height="2" fill="#fff" opacity="0.18" />
      </g>

      {/* Tassel */}
      <g transform="translate(195 100)">
        <line x1="0" y1="0" x2="0" y2="22" stroke={ink} strokeWidth="0.8" />
        <circle cx="0" cy="22" r="3" fill={ink} />
        {[-5, -3, -1, 1, 3, 5].map((dx) => (
          <line key={dx} x1={dx} y1="24" x2={dx * 1.3} y2="42" stroke={accent} strokeWidth="1.1" />
        ))}
        <ellipse cx="0" cy="44" rx="6" ry="2" fill={accent} opacity="0.25" />
      </g>
    </svg>
  );
}

// ─── Hairline divider ─────────────────────────────────────────────────
function HairlineDivider({ ink, accent }: { ink: string; accent: string }) {
  return (
    <div
      aria-hidden
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 24px",
        gap: 16,
      }}
    >
      <div style={{ flex: 1, maxWidth: 120, height: 1, background: ink, opacity: 0.35 }} />
      <O.Diamond size={10} color={accent} />
      <div style={{ flex: 1, maxWidth: 120, height: 1, background: ink, opacity: 0.35 }} />
    </div>
  );
}

// ─── Achievement icons ────────────────────────────────────────────────
function GpaIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
      <path d="M3 6 L 10 3 L 17 6 L 10 9 Z" stroke={color} strokeWidth="0.9" fill="none" />
      <path d="M6 7.5 L 6 12 Q 10 14 14 12 L 14 7.5" stroke={color} strokeWidth="0.9" fill="none" />
      <line x1="17" y1="6" x2="17" y2="11" stroke={color} strokeWidth="0.9" />
    </svg>
  );
}
function TrophyIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
      <path d="M6 4 L 14 4 L 14 8 Q 14 12 10 12 Q 6 12 6 8 Z" stroke={color} strokeWidth="0.9" fill="none" />
      <path d="M6 6 Q 3 6 3 8 Q 3 9 5 10" stroke={color} strokeWidth="0.9" fill="none" />
      <path d="M14 6 Q 17 6 17 8 Q 17 9 15 10" stroke={color} strokeWidth="0.9" fill="none" />
      <line x1="10" y1="12" x2="10" y2="15" stroke={color} strokeWidth="0.9" />
      <line x1="7" y1="16" x2="13" y2="16" stroke={color} strokeWidth="0.9" />
    </svg>
  );
}
function BookIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
      <path d="M3 4 Q 7 3 10 5 Q 13 3 17 4 L 17 16 Q 13 15 10 16 Q 7 15 3 16 Z" stroke={color} strokeWidth="0.9" fill="none" />
      <line x1="10" y1="5" x2="10" y2="16" stroke={color} strokeWidth="0.7" opacity="0.6" />
    </svg>
  );
}
function LaurelIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
      <path d="M5 4 Q 4 10 10 16 Q 16 10 15 4" stroke={color} strokeWidth="0.9" fill="none" />
      <path d="M6 7 Q 4 7 4 9 M 14 7 Q 16 7 16 9 M 7 11 Q 4 11 4 13 M 13 11 Q 16 11 16 13" stroke={color} strokeWidth="0.7" fill="none" />
      <circle cx="10" cy="16" r="1" fill={color} />
    </svg>
  );
}
function ChairIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
      <circle cx="10" cy="6" r="2.4" stroke={color} strokeWidth="0.9" fill="none" />
      <path d="M5 16 Q 5 10 10 10 Q 15 10 15 16" stroke={color} strokeWidth="0.9" fill="none" />
      <line x1="3" y1="16" x2="17" y2="16" stroke={color} strokeWidth="0.9" />
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// VARIANT
// ═══════════════════════════════════════════════════════════════════════
export function AcademiaVariant({ t, isDark, ctx }: VariantProps) {
  const tt = useT();
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();

  // Hero seal "stamping" — opacity 0 → 1 + scale 0.9 → 1 (1.2s ease-out),
  // then the diploma-scroll line unrolls beneath via clip-path expansion
  // (1.0s ease-out, delayed 0.6s).
  const [stamped, setStamped] = useState(reduce);
  const [unrolled, setUnrolled] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setStamped(true);
      setUnrolled(true);
      return;
    }
    const a = window.requestAnimationFrame(() => setStamped(true));
    const b = window.setTimeout(() => setUnrolled(true), 700);
    return () => {
      window.cancelAnimationFrame(a);
      window.clearTimeout(b);
    };
  }, [reduce]);

  // Sound — RSVP yes = chime, RSVP no + CTA tap = tap.
  const baseSetRsvp = ctx.rsvp.setRsvp;
  const setRsvpWithSound = useCallback(
    (v: string | null) => {
      if (v === "yes") play("chime");
      else if (v === "no") play("tap");
      baseSetRsvp(v);
    },
    [baseSetRsvp, play],
  );

  const isMobile = useCallback(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  }, []);

  const onCtaTap = useCallback(() => {
    if (isMobile()) return;
    play("tap");
  }, [play, isMobile]);

  // Achievements — Sevinch's academic record at TUIT.
  const achievements: Array<{
    label: string;
    detail: string;
    icon: (props: { color: string }) => JSX.Element;
  }> = [
    { label: tt("variant.g01.achievements.0.label"), detail: tt("variant.g01.achievements.0.detail"), icon: GpaIcon },
    { label: tt("variant.g01.achievements.1.label"), detail: tt("variant.g01.achievements.1.detail"), icon: TrophyIcon },
    { label: tt("variant.g01.achievements.2.label"), detail: tt("variant.g01.achievements.2.detail"), icon: BookIcon },
    { label: tt("variant.g01.achievements.3.label"), detail: tt("variant.g01.achievements.3.detail"), icon: LaurelIcon },
    { label: tt("variant.g01.achievements.4.label"), detail: tt("variant.g01.achievements.4.detail"), icon: ChairIcon },
  ];

  return (
    <div className="academia-variant">
      <style>{`
        .academia-variant { background: ${t.bg}; color: ${t.ink}; }
        .academia-variant button:focus-visible,
        .academia-variant a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .academia-variant .academia-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
            text-align: center !important;
          }
          .academia-variant .academia-name {
            font-size: clamp(36px, 12vw, 72px) !important;
            letter-spacing: 0.05em !important;
          }
          .academia-variant .academia-eyebrow { font-size: 10px !important; letter-spacing: 0.32em !important; }
          .academia-variant .academia-diploma { width: 160px !important; height: auto !important; }
          /* The Wreath SVG is size 360 and the inner hairline ring is
             280x280px — both hard-coded px that overflow a 390px viewport.
             Cap them via the academia-seal-wreath / academia-seal-ring
             classes added inline below. */
          .academia-variant .academia-seal-wreath svg {
            width: min(300px, 86vw) !important;
            height: min(300px, 86vw) !important;
          }
          .academia-variant .academia-seal-ring {
            width: min(240px, 72vw) !important;
            height: min(240px, 72vw) !important;
          }
          .academia-variant .academia-ach-row {
            grid-template-columns: 32px 1fr !important;
            gap: 14px !important;
            padding: 18px 0 !important;
          }
          .academia-variant .academia-ach-num { display: none !important; }
          .academia-variant .academia-ach-section { padding: 64px 18px !important; }
          .academia-variant .academia-ach-prose { max-width: 560px !important; }
          .academia-variant button,
          .academia-variant a[role="button"] {
            min-height: 44px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .academia-variant *,
          .academia-variant *::before,
          .academia-variant *::after {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {/* ─── HERO ────────────────────────────────────────────────────── */}
      <section
        className="academia-hero"
        style={{
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          padding: "80px 32px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          background: t.bg,
          color: t.ink,
        }}
      >
        {/* Eyebrow — institution */}
        <div
          className="academia-eyebrow"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: t.ink,
            opacity: 0.75,
            marginBottom: 8,
          }}
        >
          {tt("variant.g01.hero.institution")}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5em",
            color: t.accent,
            marginBottom: 32,
          }}
        >
          · TUIT ·
        </div>

        {/* Academic seal — wreath that "stamps" itself in around the name */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            padding: "24px 0",
          }}
        >
          {/* Wreath behind the name (the seal) */}
          <div
            aria-hidden
            className="academia-seal-wreath"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: stamped ? 0.55 : 0,
              transform: stamped ? "scale(1)" : "scale(0.9)",
              transition: reduce ? "none" : "opacity 1.2s ease-out, transform 1.2s ease-out",
              willChange: reduce ? undefined : "opacity, transform",
            }}
          >
            <O.Wreath size={360} color={t.ink} />
          </div>
          {/* Inner red hairline ring */}
          <div
            aria-hidden
            className="academia-seal-ring"
            style={{
              position: "absolute",
              width: 280,
              height: 280,
              borderRadius: "50%",
              border: `0.6px solid ${t.accent}`,
              opacity: stamped ? 0.5 : 0,
              transition: reduce ? "none" : "opacity 1.2s ease-out 0.2s",
            }}
          />

          {/* Eyebrow */}
          <div style={{ position: "relative", padding: "0 24px" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.4em",
                color: t.accent,
                marginBottom: 18,
              }}
            >
              {tt("variant.g01.hero.eyebrow")}
            </div>
            <h1
              className="academia-name"
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontSize: "clamp(56px, 9vw, 104px)",
                lineHeight: 1.02,
                fontWeight: 500,
                letterSpacing: "0.08em",
                color: t.ink,
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              {(() => {
                // The graduate's name renders across two lines (given name /
                // family name). Split t.names on whitespace; first token sits
                // on line 1, the remainder on line 2. Single-word names render
                // on one line.
                const parts = (t.names || "").trim().split(/\s+/).filter(Boolean);
                if (parts.length <= 1) return t.names;
                return (
                  <>
                    {parts[0]}
                    <br />
                    {parts.slice(1).join(" ")}
                  </>
                );
              })()}
            </h1>
            <div
              style={{
                fontFamily: t.font || "var(--font-display)",
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 400,
                fontStyle: "italic",
                letterSpacing: "0.15em",
                color: t.accent,
                marginTop: 16,
              }}
            >
              MMXXVI
            </div>
          </div>
        </div>

        {/* Diploma scroll line that "unrolls" beneath the name */}
        <div
          style={{
            marginTop: 48,
            width: "min(420px, 80%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            clipPath: unrolled ? "inset(0 0% 0 0%)" : "inset(0 50% 0 50%)",
            transition: reduce ? "none" : "clip-path 1.0s ease-out",
            willChange: reduce ? undefined : "clip-path",
          }}
        >
          {/* Top hairline */}
          <div style={{ height: 1, width: "100%", background: t.ink, opacity: 0.5 }} />
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.4em",
              color: t.ink,
              padding: "16px 0",
            }}
          >
            {t.date} · {t.venue.toUpperCase()}
          </div>
          <div style={{ height: 1, width: "100%", background: t.ink, opacity: 0.5 }} />
        </div>

        {/* Diploma + tassel illustration */}
        <div
          className="academia-diploma"
          style={{
            marginTop: 48,
            opacity: unrolled ? 1 : 0,
            transform: unrolled ? "translateY(0)" : "translateY(12px)",
            transition: reduce ? "none" : "opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s",
          }}
        >
          <DiplomaIllustration ink={t.ink} accent={t.accent} size={220} />
        </div>
      </section>

      {/* ─── COUNTDOWN ───────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── STORY ───────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow={tt("variant.g01.story.eyebrow")}
          title={tt("variant.g01.story.title")}
          body={tt("variant.g01.story.body")}
        />
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── ACHIEVEMENTS — unique academic-list section ─────────────── */}
      <Reveal reduce={reduce}>
        <section
          className="academia-ach-section"
          style={{
            padding: "120px 48px",
            borderTop: `1px solid ${t.ink}22`,
          }}
        >
          <div className="academia-ach-prose" style={{ maxWidth: 760, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: t.accent,
                  marginBottom: 16,
                }}
              >
                {tt("variant.g01.achievements.eyebrow")}
              </div>
              <h2
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 500,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                {tt("variant.g01.achievements.title")}
              </h2>
              <div
                style={{
                  width: 80,
                  height: 1,
                  background: t.accent,
                  margin: "20px auto 0",
                  opacity: 0.7,
                }}
              />
            </div>

            <ol
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              {achievements.map((a, i) => {
                const Icon = a.icon;
                return (
                  <li
                    key={a.label}
                    className="academia-ach-row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "44px 32px 1fr",
                      alignItems: "center",
                      gap: 24,
                      padding: "24px 0",
                      borderTop: `1px solid ${t.ink}22`,
                      borderBottom:
                        i === achievements.length - 1 ? `1px solid ${t.ink}22` : "none",
                    }}
                  >
                    <span
                      className="academia-ach-num"
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 28,
                        fontWeight: 500,
                        color: t.accent,
                        lineHeight: 1,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      style={{
                        color: t.accent,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      aria-hidden
                    >
                      <Icon color={t.accent} />
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <span
                        style={{
                          fontFamily: t.font || "var(--font-display)",
                          fontSize: 22,
                          fontWeight: 600,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: t.ink,
                        }}
                      >
                        {a.label}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: 15,
                          color: `${t.ink}aa`,
                          fontStyle: "italic",
                        }}
                      >
                        {a.detail}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── GALLERY ─────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── SCHEDULE ────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest("button")) onCtaTap();
          }}
        >
          <ScheduleSection
            t={t}
            items={[
              ["10:00", tt("variant.g01.schedule.0.event")],
              ["11:00", tt("variant.g01.schedule.1.event")],
              ["12:30", tt("variant.g01.schedule.2.event")],
              ["13:30", tt("variant.g01.schedule.3.event")],
              ["15:00", tt("variant.g01.schedule.4.event")],
              ["18:00", tt("variant.g01.schedule.5.event")],
            ]}
          />
        </div>
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── MAP ─────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.closest("a") || target.closest("button"))) onCtaTap();
          }}
        >
          <MapSection
            t={t}
            isDark={isDark}
            addressLine={tt("variant.g01.map.address")}
          />
        </div>
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── RSVP ────────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={setRsvpWithSound}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label={tt("variant.g01.rsvp.label")}
        />
      </Reveal>

      <HairlineDivider ink={t.ink} accent={t.accent} />

      {/* ─── SHARE ───────────────────────────────────────────────────── */}
      <Reveal reduce={reduce}>
        <div
          onClick={(e) => {
            const target = e.target as HTMLElement | null;
            if (target && target.closest("button")) onCtaTap();
          }}
        >
          <ShareSection t={t} />
        </div>
      </Reveal>

      <GuestFooter t={t} />
    </div>
  );
}
